import { useContext } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { FeatureFlag as FeatureGate } from '@/components/feature-flag/FeatureFlag';
import { FeatureDisabledPlaceholder } from '@/components/feature-flag/FeatureDisabledPlaceholder';
import { flagsForPath } from '@/lib/feature-flags/module-routes';
import { FeatureFlagContext } from '@/providers/FeatureFlagContext';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { useUIStore } from '@/stores/ui.store';
import { EUserRole } from '@/types';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { TenantSuspendedBanner } from '@/components/shared/tenant-suspended-banner';
import { InspectorAccessDisabled } from '@/components/shared/inspector-access-disabled';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FeatureFlag } from '@/lib/feature-flags/keys';
import { RealtimeNotifications } from '@/features/notifications/hooks/useRealtimeNotifications';
import { cn } from '@/lib/utils';
import { MINE_SETUP_PERMISSIONS, usePermissions } from '@/lib/permissions';
import { kybService } from '@/features/tenant-config/services/kyb.service';
import { tenantSettingsService } from '@/features/tenant-config/services/tenant-settings.service';

export function DashboardLayout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const activeRole = useAuthStore((s) => s.activeRole);
  const kybStatus = useAuthStore((s) => s.kybStatus);
  const mustChangePassword = useAuthStore((s) => s.mustChangePassword);
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const location = useLocation();

  const isITAdmin = activeRole === EUserRole.IT_ADMINISTRATOR;
  const isMineManager = activeRole === EUserRole.MINE_MANAGER;
  const isInspector = activeRole === EUserRole.GOVERNMENT_INSPECTOR;
  const tenantId = user?.tenantId;
  const kybApproved = kybStatus === 'APPROVED';
  const isOnKybPage = location.pathname === '/admin/kyb';
  const isOnSetupWizard = location.pathname === '/admin/mine-setup';
  const isOnInspectorOnboarding = location.pathname === '/admin/inspector-onboarding';
  const featureFlags = useContext(FeatureFlagContext);
  // When the flags could not be fetched (offline field users, a network blip)
  // the page opens and the API decides, as it did before pages were gated.
  const requiredFlags = featureFlags.isError ? [] : flagsForPath(location.pathname);
  const mustCompleteMineSetup = useAuthStore((s) => s.mustCompleteMineSetup);
  const { canAll } = usePermissions();
  const mustCompleteInspectorProfile = useAuthStore((s) => s.mustCompleteInspectorProfile);
  const ONBOARDING_LANDING_PATHS = new Set([
    '/',
    '/admin',
    '/admin/',
    '/admin/dashboard',
  ]);
  const isOnOnboardingLanding = ONBOARDING_LANDING_PATHS.has(location.pathname);

  // Keep KYB status in sync for IT admins
  useQuery({
    queryKey: ['tenant-kyb-status', tenantId],
    queryFn: async () => {
      if (!tenantId) throw new Error('Missing tenantId');
      const result = await kybService.getTenantKyb(tenantId);
      if (result.kybStatus !== kybStatus) {
        useAuthStore.setState({ kybStatus: result.kybStatus as typeof kybStatus });
      }
      return result;
    },
    enabled: isAuthenticated && isITAdmin && !!tenantId && !kybApproved,
    refetchInterval: 30000,
  });

  // Only fires on dashboard landing paths, so the user can still navigate to complete checklist items.
  const { data: tenantSettings } = useQuery({
    queryKey: ['tenant-settings', tenantId],
    queryFn: () => tenantSettingsService.get(),
    enabled: isITAdmin && !!tenantId && kybApproved,
    staleTime: 60_000,
  });
  const needsOnboarding =
    isITAdmin && kybApproved && tenantSettings?.onboardingComplete === false;

  // Backend 403s every tenant-data request when government_inspector_access is off; show the reason instead of a broken shell.
  const { enabled: inspectorAccessEnabled, isResolved: flagsResolved } =
    useFeatureFlag(FeatureFlag.GOVERNMENT_INSPECTOR_ACCESS);
  const inspectorAccessBlocked =
    isInspector && flagsResolved && !inspectorAccessEnabled;

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  if (mustChangePassword) {
    return <Navigate to="/auth/set-password" replace />;
  }

  if (isITAdmin && !kybApproved && !isOnKybPage) {
    return <Navigate to="/admin/kyb" replace />;
  }

  if (needsOnboarding && isOnOnboardingLanding) {
    return <Navigate to="/admin/config/onboarding" replace />;
  }

  // Runs even if the tenant's mine is already configured — Mine Manager reviews and clicks Complete to clear the flag.
  // Only when they can open it: the wizard is closed to someone missing one of its
  // permissions, and sending them there would bounce them straight back here.
  if (isMineManager && mustCompleteMineSetup && canAll(MINE_SETUP_PERMISSIONS) && !isOnSetupWizard) {
    return <Navigate to="/admin/mine-setup" replace />;
  }

  // Checked ahead of onboarding — completing a profile the inspector can't use would be a dead end.
  if (inspectorAccessBlocked) {
    return (
      <InspectorAccessDisabled
        {...(user?.tenantName ? { tenantName: user.tenantName } : {})}
      />
    );
  }

  if (isInspector && mustCompleteInspectorProfile && !isOnInspectorOnboarding) {
    return <Navigate to="/admin/inspector-onboarding" replace />;
  }

  return (
    <div className="min-h-screen bg-muted print:bg-white">
      <RealtimeNotifications />
      <div className="print:hidden">
        <Sidebar />
      </div>
      <div
        className={cn(
          'transition-all duration-300 print:ml-0',
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64',
        )}
      >
        <div className="sticky top-0 z-30 print:hidden">
          <Header />
          <TenantSuspendedBanner />
        </div>
        <main className="p-4 md:p-6 print:p-0">
          {requiredFlags.reduceRight(
            (page, flag) => (
              <FeatureGate flag={flag} fallback={<FeatureDisabledPlaceholder />}>
                {page}
              </FeatureGate>
            ),
            <Outlet />,
          )}
        </main>
      </div>
    </div>
  );
}

/** Alias for backward compat */
export { DashboardLayout as PortalLayout };
